import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import QRCode from 'qrcode';
import { CheckCircle, Smartphone, AlertCircle, Loader2 } from 'lucide-react';
import { createScanLoginScene, pollScanLoginScene } from '../../api/scanLogin';
import { useAuthStore, type User } from '../../stores/authStore';
import { get } from '../../api/client';

interface ScanLoginQrCodeProps {
  onSuccess?: () => void;
}

type QrStatus = 'loading' | 'ready' | 'scanned' | 'confirmed' | 'expired' | 'error';

const AUTO_REFRESH_DELAY_MS = 1200;
const MAX_POLL_FAILURES = 3;
/** 本标签页当前挂着的那张码，见 initScene 里的回传 */
const LAST_SCENE_KEY_STORAGE = 'scan-login:last-scene-key';

export default function ScanLoginQrCode({ onSuccess }: ScanLoginQrCodeProps) {
  const navigate = useNavigate();
  const [qrDataUrl, setQrDataUrl] = useState<string>('');
  const [status, setStatus] = useState<QrStatus>('loading');
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const autoRefreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollFailCountRef = useRef(0);
  const isAutoRefreshingRef = useRef(false);
  const mountedRef = useRef(true);
  // 当前场景的过期时间戳（本地兜底判定：pollScene 返回的 status 可能滞后）
  const expireAtMsRef = useRef<number>(0);
  // initScene 与 startPolling 互相调用（轮询到"已取消/已过期"需重建场景），
  // 通过 ref 解开 useCallback 循环依赖
  const initSceneRef = useRef<() => void>(() => {});

  const clearTimers = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    if (autoRefreshTimerRef.current) {
      clearTimeout(autoRefreshTimerRef.current);
      autoRefreshTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      clearTimers();
    };
  }, [clearTimers]);

  // 过期自动刷新：短暂展示"正在自动刷新"提示后重建场景（无按钮、无倒计时）
  const scheduleAutoRefresh = useCallback(
    (delayMs = AUTO_REFRESH_DELAY_MS) => {
      if (isAutoRefreshingRef.current) return;
      isAutoRefreshingRef.current = true;
      clearTimers();
      autoRefreshTimerRef.current = setTimeout(() => {
        isAutoRefreshingRef.current = false;
        if (mountedRef.current) initSceneRef.current();
      }, delayMs);
    },
    [clearTimers],
  );

  const handleLoginSuccess = useCallback(
    async (tokens: { accessToken: string }) => {
      setStatus('confirmed');
      clearTimers();

      try {
        // 用 accessToken 拉取用户信息，建立完整会话（refreshToken 的 HttpOnly Cookie 已由 poll 端点写入）
        const user = await get<User>('/auth/me');
        if (!user || !user.id) {
          throw new Error('获取用户信息失败');
        }

        const authStore = useAuthStore.getState();
        await authStore.applyTokenPair({ accessToken: tokens.accessToken }, user);

        if (onSuccess) {
          onSuccess();
        } else {
          navigate('/', { replace: true });
        }
      } catch (e) {
        console.error('[ScanLogin] 建立会话失败:', e);
        setStatus('error');
      }
    },
    [navigate, onSuccess, clearTimers],
  );

  const startPolling = useCallback(
    (key: string) => {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);

      pollTimerRef.current = setInterval(async () => {
        // 本地过期兜底：expire_at 已过时直接刷新，不依赖服务端状态标记
        if (expireAtMsRef.current && Date.now() >= expireAtMsRef.current) {
          setStatus('expired');
          scheduleAutoRefresh();
          return;
        }

        try {
          const result = await pollScanLoginScene(key);
          pollFailCountRef.current = 0;

          switch (result.status) {
            case 'scanned':
              setStatus('scanned');
              break;
            case 'confirmed':
              if (result.accessToken) {
                await handleLoginSuccess({ accessToken: result.accessToken });
              }
              break;
            case 'cancelled':
              initSceneRef.current();
              break;
            case 'expired':
              setStatus('expired');
              scheduleAutoRefresh();
              break;
          }
        } catch (e) {
          console.error('[ScanLogin] poll error:', e);
          // 429 限流：不是场景失效，静默跳过本轮，等限流窗口恢复后自然继续
          if ((e as { status?: number } | null | undefined)?.status === 429) return;
          pollFailCountRef.current += 1;
          // 场景记录被清理（404）或网络持续异常时兜底自动重建，避免二维码僵死
          if (pollFailCountRef.current >= MAX_POLL_FAILURES) {
            pollFailCountRef.current = 0;
            scheduleAutoRefresh();
          }
        }
      }, 2000);
    },
    [handleLoginSuccess, scheduleAutoRefresh],
  );

  const initScene = useCallback(async () => {
    clearTimers();
    setStatus('loading');
    try {
      // 把本标签页上一张码交回服务端作废。存在 sessionStorage 而不是组件 ref 里，
      // 是因为「上一张」的边界是标签页：自动刷新、切成账号登录再切回来、创建失败后点重新加载，
      // 都要作废同一张；而另一个标签页的码与本页互不相干，按 sessionStorage 的天然隔离正好放过它。
      // 不这么做的话，屏幕上换了码、手机上还停着旧确认页，用户按下确认只会换来一个没人轮询的会话。
      const previousKey = sessionStorage.getItem(LAST_SCENE_KEY_STORAGE) || undefined;
      const { sceneKey: key, expireAt } = await createScanLoginScene(previousKey);
      sessionStorage.setItem(LAST_SCENE_KEY_STORAGE, key);
      expireAtMsRef.current = new Date(expireAt).getTime();

      const baseUrl = window.location.origin;
      const qrContent = `${baseUrl}/scan-login?sceneKey=${key}`;
      const dataUrl = await QRCode.toDataURL(qrContent, {
        width: 200,
        margin: 2,
        color: { dark: '#000000', light: '#ffffff' },
      });
      setQrDataUrl(dataUrl);
      setStatus('ready');

      startPolling(key);
    } catch (e) {
      console.error('[ScanLogin] init error:', e);
      setStatus('error');
    }
  }, [startPolling, clearTimers]);

  useEffect(() => {
    initSceneRef.current = initScene;
  }, [initScene]);

  const handleRetry = useCallback(() => {
    initScene();
  }, [initScene]);

  // 只在挂载时建一次场景，所以刻意不挂 [initScene]：initScene 的身份会沿
  // startPolling → handleLoginSuccess → onSuccess 这条链变化（父组件一旦传行内回调，
  // 每渲染一次就换一个），挂在依赖上等于「父级一渲染就 clearTimers + 换一张新码」——
  // 用户正对准的那张码会被换掉，手机扫到的 key 与页面轮询的 key 就此错开。
  useEffect(() => {
    initSceneRef.current();
  }, []);

  return (
    <div className="space-y-5">
      <div className="flex justify-center">
        <div className="relative p-4 bg-white border border-slate-200 rounded-lg shadow-sm">
          {status === 'loading' && (
            <div className="w-[200px] h-[200px] flex items-center justify-center">
              <Loader2 className="h-8 w-8 text-slate-400 animate-spin" />
            </div>
          )}

          {status === 'ready' && qrDataUrl && (
            <img src={qrDataUrl} alt="扫码登录二维码" className="w-[200px] h-[200px]" />
          )}

          {status === 'scanned' && qrDataUrl && (
            <>
              <img src={qrDataUrl} alt="扫码登录二维码" className="w-[200px] h-[200px] opacity-30" />
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/80 rounded-lg">
                <CheckCircle className="h-10 w-10 text-teal-500 mb-2" />
                <span className="text-sm font-medium text-teal-600">扫描成功</span>
                <span className="text-xs text-slate-500 mt-1">请在手机上确认登录</span>
              </div>
            </>
          )}

          {status === 'confirmed' && (
            <div className="w-[200px] h-[200px] flex flex-col items-center justify-center">
              <CheckCircle className="h-12 w-12 text-teal-500 mb-2" />
              <span className="text-sm font-medium text-teal-600">登录成功</span>
              <span className="text-xs text-slate-500 mt-1">正在跳转...</span>
            </div>
          )}

          {status === 'expired' && (
            <div className="w-[200px] h-[200px] flex flex-col items-center justify-center">
              <Loader2 className="h-10 w-10 text-slate-400 animate-spin mb-2" />
              <span className="text-sm text-slate-500">二维码已过期，正在自动刷新</span>
            </div>
          )}

          {status === 'error' && (
            <div className="w-[200px] h-[200px] flex flex-col items-center justify-center">
              <AlertCircle className="h-10 w-10 text-red-400 mb-2" />
              <span className="text-sm text-slate-500">加载失败</span>
              <button
                onClick={handleRetry}
                className="mt-3 px-4 py-1.5 text-xs font-medium text-white bg-teal-500 rounded-md hover:bg-teal-600 transition-colors"
              >
                重新加载
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="text-center">
        {(status === 'ready' || status === 'scanned') && (
          <div className="flex items-center justify-center gap-1.5 text-sm text-slate-500">
            <Smartphone className="h-4 w-4" />
            <span>请使用手机APP扫码登录</span>
          </div>
        )}
      </div>
    </div>
  );
}
